import { HardwareItemUnit } from "../enums/hardware-item-unit.enum";
import { PurchaseRequestTimelineEventType } from "../enums/purchase-request-timeline-event-type.enum";
import { PurchaseRequestLeadTimeSource } from "../enums/purchase-request-lead-time-source.enum";
import { PurchaseRequestType } from "../enums/purchase-request-type.enum";
import { DisplayPurchaseOrder, PurchaseOrder } from "./purchase-order.model";
import { UploadedFile } from "./uploaded-file.model";
import { UserComment } from "./user-comment.model";

interface PurchaseRequest_Base {
    id: string;
    vendorId: string;
    needDate: Date;
    quantity: number;
    units: HardwareItemUnit;
    unitPrice: number;
    purchaseRequestListId: string;
    files: UploadedFile[];
    comments: UserComment[];
    productUrl: string;
    readiedById: string;
    readiedOn: Date | null;
    createdById: string;
    createdOn: Date;
    canceledOn: Date | null;
    canceledById: string;
    changeLog: PurchaseRequestChangeLog[];

    // used for query on dashboard (bool on if it has been ordered, even partially)
    ordered: boolean;
    completedOn: Date | null;
    purchaseRequestType: PurchaseRequestType;

    /* ==================== PURCHASING PLANNER ====================
       The planner is a gantt on the purchasing page: x-axis weeks, y-axis requests, and the agent
       drags one mark per request to decide WHEN to order it against a weekly budget cap.

       These live ON THE REQUEST rather than in a separate `purchasing-planner-entry` collection
       (reversed 2026-08-05; the old model file and its collection are removed). One row, one
       document, one live stream — the planner and the list are the same page now, so a second
       collection meant joining two streams to draw one row.

       ⚠ WRITE THESE WITH updatePurchaseRequest(), NEVER setPurchaseRequest(). The latter is a
       full-document set called from ~14 places, and the codebase already documents two browsers
       clobbering each other through it (see the comment on appendPurchaseRequestComment). The
       planner patches only the fields it owns; that is what keeps a plan from being erased by
       someone editing a quantity in another tab. */

    /** The day the order should be placed. Null = not on the plan yet; it sits in the intake tray
     *  with a SUGGESTED date derived from needDate − leadTime − buffer, which is not persisted
     *  until the agent actually plans it. */
    plannedOrderDate: Date | null;

    /* ---- LEAD TIME: THE ANSWER AND WHERE IT CAME FROM ----
       Stored as a pair, and the pair is the point. The number alone drives the geometry — the bar
       length, the projected arrival, the slack against the need date — but it cannot say how much
       to trust itself. Eleven days derived from four closed orders and eleven typed in after a
       phone call are the same number and not the same fact.
       The source also drives the UI directly: it selects which of the request panel's three
       lead-time tabs opens. Without it that selection is per-session state, and an agent who
       worked out a lead time on tuesday comes back on wednesday to a default tab with no sign of
       what they decided.

       INVARIANT: the two are set and cleared TOGETHER. Both null is the "no lead time" state — the
       chart draws a dashed bar with no arrival mark, because there is genuinely nothing to
       project. Neither should ever be null while the other is not.

       SNAPSHOT, NOT A LIVE LINK. Choosing "vendor standard" copies the vendor's number here rather
       than pointing at it, so a vendor edited next month does not silently move the arrival dates
       of everything already planned against it. Re-picking the tab is what refreshes the copy —
       an explicit gesture, which is the only kind that should move a date on a plan. */
    leadTimeBusinessDays: number | null;
    leadTimeSource: PurchaseRequestLeadTimeSource | null;

    /** "Outside budget": still planned and still in the week's work list, but excluded from the
     *  weekly cap and totalled separately — an emergency buy that must not strangle the week. */
    budgetExempt: boolean;

    /* ---- THE NOTE ----
       The column the spreadsheet has and this app did not: one free-text line about the request,
       overwritten whenever it stops being true. "waiting on a quote", "Bob says hold until the
       september price list", "backordered — recheck on the 25th".

       IT IS NOT A COMMENT, and the distinction is the whole reason it needs its own field.
       `comments` above is a thread: attributed, timestamped, append-only, and something the other
       people on the request get notified about. You cannot correct one, and reading a thread is not
       the same act as glancing at a column. This is a scratch line with exactly one current value,
       which is what makes it the thing the sheet actually had.

       ⚠ Written from the planner panel through updatePurchaseRequest() — the partial patch — never
       setPurchaseRequest(). Same rule as the planner fields above and for the same reason. */
    note: string;

    /** Who last wrote the note and when. A free-text field with no stamp is the one thing nobody can
     *  tell is stale, and going stale is how every note in the sheet eventually failed. Both are
     *  cleared with the note when it is emptied, so a blank note never carries an author. */
    noteUpdatedById: string;
    noteUpdatedOn: Date | null;
}

export interface PurchaseRequest_PlasticsHardware extends PurchaseRequest_Base {
    hardwareId: string;
    vendorHardwareDataId: string;
    purchaseRequestType: PurchaseRequestType.Plastics;
}

export interface PurchaseRequest_MetalsHardware extends PurchaseRequest_Base {
    hardwareId: string;
    purchaseRequestType: PurchaseRequestType.Metals
}

export interface PurchaseRequest_C3Hardware extends PurchaseRequest_Base {
    hardwareId: string;
    vendorHardwareDataId: string;
    purchaseRequestType: PurchaseRequestType.C3;
}

export interface PurchaseRequest_Custom extends PurchaseRequest_Base {
    partNumber: string;
    description: string;
    purchaseRequestType: PurchaseRequestType.Custom
}

export type PurchaseRequest = PurchaseRequest_PlasticsHardware | PurchaseRequest_MetalsHardware | PurchaseRequest_C3Hardware | PurchaseRequest_Custom

export interface PurchaseRequestChangeLog {
    id: string;
    createdOn: Date;
    createdById: string;
    text: string;
}

export const DEFAULT_PURCHASE_REQUEST: PurchaseRequest = {
    id: "",
    vendorId: "",
    needDate: new Date(),
    quantity: 0,
    units: HardwareItemUnit.Pieces,
    unitPrice: 0,
    purchaseRequestListId: "",
    files: [],
    comments: [],
    productUrl: "",
    readiedById: "",
    readiedOn: null,
    createdById: "",
    createdOn: new Date(),
    canceledOn: null,
    canceledById: "",
    ordered: false,
    completedOn: null,
    changeLog: [],
    purchaseRequestType: PurchaseRequestType.Custom,
    partNumber: "",
    description: "",
    // a new request starts unplanned and with no lead time: it lands in the planner's intake tray
    // rather than on a week, and draws a dashed bar with no projected arrival until one is set
    plannedOrderDate: null,
    leadTimeBusinessDays: null,
    leadTimeSource: null,
    budgetExempt: false,
    note: "",
    noteUpdatedById: "",
    noteUpdatedOn: null,
}
